// Contact Lists management page.
//
// Lists all contact lists in the workspace; create / edit / delete /
// open detail. Wraps `<PageShell>` per the project DS rules.
//
// The "+ New list" CTA opens the ContactListComposer — a modal that
// collapses naming and populating into one moment (Static gets a
// multi-select picker; Dynamic gets a filter editor with live preview).
// Click a row to navigate to the detail page (`/contact-lists/:id`)
// for ongoing member management on static lists.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Plus, Trash2, Users } from 'lucide-react';

import PageLoader from '../../components/PageLoader';
import PageShell from '../../components/PageShell';
import { contactListsAPI } from '../../services/contactListsAPI';
import { crmSyncAPI } from '../../services/crmSyncService';
import {
  ContactList,
  ContactListCreate,
  ContactListUpdate,
} from '../../types/contactList';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Pill,
  type ColumnDef,
} from '../../ui';
import ContactListComposer, {
  type ContactListComposerSubmit,
} from './ContactListComposer';

type ComposerMode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; list: ContactList };

const ContactListsPage: React.FC = () => {
  const navigate = useNavigate();
  const [lists, setLists] = useState<ContactList[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [composer, setComposer] = useState<ComposerMode>({ kind: 'closed' });
  const [submitting, setSubmitting] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const rows = await contactListsAPI.list({ limit: 100 });
      setLists(rows);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load contact lists');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleSubmit = useCallback(
    async (submission: ContactListComposerSubmit) => {
      setSubmitting(true);
      setComposerError(null);
      try {
        if (submission.mode === 'create') {
          const created = await contactListsAPI.create(
            submission.payload as ContactListCreate,
          );
          if (
            submission.initialMemberIds &&
            submission.initialMemberIds.length > 0
          ) {
            try {
              await contactListsAPI.addMembers(created.id, {
                contact_ids: submission.initialMemberIds,
              });
            } catch (memberErr) {
              // Best-effort: list got created, but adding initial
              // members failed. Surface a non-fatal error.
              setErr(
                memberErr instanceof Error
                  ? `List created, but adding members failed: ${memberErr.message}`
                  : 'List created, but adding members failed',
              );
            }
          }
          // LT-397 — From-CRM: list is created with type='static'; now
          // pull the picked CRM rows in. Best-effort like add-members:
          // import failures don't undo the list creation, but they're
          // surfaced so the operator sees the error count and can retry.
          if (
            submission.crmImport &&
            submission.crmImport.externalIds.length > 0
          ) {
            try {
              const importResult = await crmSyncAPI.importToList(created.id, {
                external_ids: submission.crmImport.externalIds,
                dedupe: submission.crmImport.dedupe,
              });
              if (importResult.errors.length > 0) {
                setErr(
                  `List created. Imported ${importResult.imported}, `
                  + `skipped ${importResult.skipped_dupe} duplicate(s), `
                  + `${importResult.errors.length} failed.`,
                );
              }
            } catch (importErr) {
              setErr(
                importErr instanceof Error
                  ? `List created, but CRM import failed: ${importErr.message}`
                  : 'List created, but CRM import failed',
              );
            }
          }
        } else if (composer.kind === 'edit') {
          await contactListsAPI.update(
            composer.list.id,
            submission.payload as ContactListUpdate,
          );
        }
        setComposer({ kind: 'closed' });
        await reload();
      } catch (e) {
        setComposerError(e instanceof Error ? e.message : 'Save failed');
      } finally {
        setSubmitting(false);
      }
    },
    [composer, reload],
  );

  const handleDelete = useCallback(
    async (cl: ContactList) => {
      if (!window.confirm(`Delete contact list "${cl.name}"?`)) return;
      try {
        await contactListsAPI.remove(cl.id);
        await reload();
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Failed to delete');
      }
    },
    [reload],
  );

  const columns = useMemo<ColumnDef<ContactList>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        sortValue: (row) => row.name.toLowerCase(),
        accessor: (row) => (
          <span className="font-medium text-slate-50">{row.name}</span>
        ),
      },
      {
        id: 'list_type',
        header: 'Type',
        sortable: true,
        sortValue: (row) => row.list_type,
        accessor: (row) => (
          <Pill intent={row.list_type === 'dynamic' ? 'info' : 'neutral'}>
            {row.list_type === 'dynamic' ? 'Dynamic' : 'Static'}
          </Pill>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        sortable: true,
        sortValue: (row) => (row.description ?? '').toLowerCase(),
        accessor: (row) => (
          <span className="text-slate-300 truncate max-w-md inline-block">
            {row.description ?? '—'}
          </span>
        ),
      },
      {
        id: 'created_at',
        header: 'Created',
        sortable: true,
        sortValue: (row) => new Date(row.created_at).getTime(),
        accessor: (row) => (
          <span className="text-slate-400 font-mono text-xs">
            {new Date(row.created_at).toLocaleDateString()}
          </span>
        ),
      },
    ],
    [],
  );

  const renderRowActions = useCallback(
    (row: ContactList) => (
      <div className="flex gap-1">
        <Button
          intent="ghost"
          aria-label={`Edit ${row.name}`}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            setComposerError(null);
            setComposer({ kind: 'edit', list: row });
          }}
        >
          <Pencil className="w-4 h-4" strokeWidth={2} />
        </Button>
        <Button
          intent="ghost"
          aria-label={`Delete ${row.name}`}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            void handleDelete(row);
          }}
        >
          <Trash2 className="w-4 h-4" strokeWidth={2} />
        </Button>
      </div>
    ),
    [handleDelete],
  );

  if (loading) return <PageLoader />;

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-8">
          <div>
            <p className="text-xs font-mono uppercase tracking-wide text-slate-400 mb-2">
              CONTACT LISTS
            </p>
            <h1 className="text-3xl font-semibold text-slate-50">Contact Lists</h1>
            <p className="text-slate-300 mt-2 max-w-2xl">
              Reusable cohorts for campaign audiences. Static lists hold an explicit
              roster; dynamic lists re-evaluate a saved filter every time they're
              consumed.
            </p>
          </div>
          <Button
            intent="primary"
            onClick={() => {
              setComposerError(null);
              setComposer({ kind: 'create' });
            }}
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} />
            New list
          </Button>
        </div>

        {err && (
          <Card className="mb-4 border-danger">
            <p className="text-danger-text">{err}</p>
          </Card>
        )}

        {lists.length === 0 ? (
          <EmptyState
            surface="contact_lists"
            icon={Users}
            headline="No contact lists yet"
            subhead="Create a static list to curate a cohort, or a dynamic list to save a filter that auto-evaluates."
            primary={{
              label: 'Create your first list',
              icon: Plus,
              onClick: () => {
                setComposerError(null);
                setComposer({ kind: 'create' });
              },
            }}
          />
        ) : (
          <Card padding="tight">
            <DataTable<ContactList>
              data={lists}
              columns={columns}
              getRowKey={(row) => row.id}
              onRowClick={(row) => navigate(`/contact-lists/${row.id}`)}
              renderRowActions={renderRowActions}
              ariaLabel="Contact lists"
            />
          </Card>
        )}

        <ContactListComposer
          open={composer.kind !== 'closed'}
          mode={composer.kind === 'edit' ? 'edit' : 'create'}
          initial={
            composer.kind === 'edit'
              ? {
                  id: composer.list.id,
                  name: composer.list.name,
                  description: composer.list.description ?? undefined,
                  list_type: composer.list.list_type,
                  filter_dsl: composer.list.filter_dsl ?? undefined,
                }
              : undefined
          }
          submitting={submitting}
          serverError={composerError}
          onSubmit={handleSubmit}
          onCancel={() => setComposer({ kind: 'closed' })}
        />
      </div>
    </PageShell>
  );
};

export default ContactListsPage;
